/**
 * The pieces used by both a company-wide page and a single project's page.
 *
 * Every form here takes an optional `lockedProjectId`. On a company-wide page
 * the project is a field somebody picks; on a project's own page it is already
 * decided and the picker would only be a way to file the record against the
 * wrong job. Sharing one component means the two routes cannot drift into
 * accepting different things.
 */
import { mul, sub, sum } from "@dantria/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useRef, useState } from "react";
import { ApiError, api, fmtAmount, formatPercent, rateFromPercent, unwrap } from "../api";
import {
  Button,
  DeleteButton,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Modal,
  Panel,
  PercentInput,
  SortTh,
  Stat,
  Table,
  TableSearch,
  Td,
  Th,
  input,
  useTable,
} from "../ui";

export interface Option {
  id: string;
  name: string;
}

export interface ProjectOption extends Option {
  originalContractValue: string;
}

export interface CodeOption {
  id: string;
  code: string;
  name: string;
}

export function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * A `<select>` that can conjure its own option. Picking the trailing "add new"
 * entry swaps the select for a name field in place, so creating a client (or a
 * contractor, or anything else that's just a name) never means leaving the
 * form that needed one. `onCreate` does the actual API call and cache
 * invalidation — this component only owns the small amount of state around
 * asking for a name and reporting whether that failed.
 */
export function PickerWithAdd({
  label,
  hint,
  error,
  value,
  onChange,
  options,
  addLabel,
  namePlaceholder = "Name",
  required,
  onCreate,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  value: string;
  onChange: (id: string) => void;
  options: Option[];
  addLabel: string;
  namePlaceholder?: string | undefined;
  required?: boolean | undefined;
  onCreate: (name: string) => Promise<Option>;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  function cancel() {
    setAdding(false);
    setName("");
    setCreateError(null);
  }

  async function add() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await onCreate(trimmed);
      onChange(created.id);
      cancel();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : "Couldn't add. Try again.");
    } finally {
      setCreating(false);
    }
  }

  if (adding) {
    return (
      <Field label={label} error={createError ?? undefined}>
        <div className="flex gap-2">
          <input
            className={input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={namePlaceholder}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
              if (e.key === "Escape") cancel();
            }}
          />
          <Button onClick={add} disabled={creating || !name.trim()}>
            {creating ? "Adding…" : "Add"}
          </Button>
          <Button variant="ghost" onClick={cancel}>
            Cancel
          </Button>
        </div>
      </Field>
    );
  }

  return (
    <Field label={label} hint={hint} error={error}>
      <select
        className={input}
        value={value}
        onChange={(e) => {
          if (e.target.value === "__new__") setAdding(true);
          else onChange(e.target.value);
        }}
        required={required}
      >
        <option value="">— pick one —</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
        <option value="__new__">{addLabel}</option>
      </select>
    </Field>
  );
}

/**
 * The client dropdown, wherever one is needed.
 *
 * `PickerWithAdd` handles the "add one in place" interaction, but every caller
 * was repeating the same three things around it: fetch the list, POST a new
 * client, invalidate the cache. That repetition is why some client dropdowns
 * could conjure a client and others couldn't — the ones nobody had got round
 * to wiring up. Binding it here means a client field is one component with one
 * required prop, and creating a client never means abandoning the form that
 * needed one.
 *
 * The query key matches every other reader of the client list, so a client
 * created from a proposal form shows up in the Clients page without a reload.
 */
export function ClientPicker({
  slug,
  value,
  onChange,
  label = "Client",
  hint,
  error,
  required,
}: {
  slug: string;
  value: string;
  onChange: (id: string) => void;
  label?: string | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
}) {
  const qc = useQueryClient();
  const clients = useQuery({
    queryKey: ["clients", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].clients.$get({ param: { slug } })),
  });

  return (
    <PickerWithAdd
      label={label}
      hint={hint}
      error={error}
      value={value}
      onChange={onChange}
      options={clients.data?.clients ?? []}
      addLabel="+ Add new client…"
      namePlaceholder="Client name"
      required={required}
      onCreate={async (name) => {
        const { client } = await unwrap(
          await api.api.c[":slug"].clients.$post({ param: { slug }, json: { name } }),
        );
        await qc.invalidateQueries({ queryKey: ["clients", slug] });
        // `.returning()` always yields the row it just inserted; the `!` marks
        // that as a typing artifact rather than a real gap.
        return client!;
      }}
    />
  );
}

/** The same, for contractors. */
export function ContractorPicker({
  slug,
  value,
  onChange,
  label = "Contractor",
  hint,
  error,
  required,
}: {
  slug: string;
  value: string;
  onChange: (id: string) => void;
  label?: string | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
}) {
  const qc = useQueryClient();
  const contractors = useQuery({
    queryKey: ["contractors", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].contractors.$get({ param: { slug } })),
  });

  return (
    <PickerWithAdd
      label={label}
      hint={hint}
      error={error}
      value={value}
      onChange={onChange}
      options={contractors.data?.contractors ?? []}
      addLabel="+ Add new contractor…"
      namePlaceholder="Contractor name"
      required={required}
      onCreate={async (name) => {
        const { contractor } = await unwrap(
          await api.api.c[":slug"].contractors.$post({ param: { slug }, json: { name } }),
        );
        await qc.invalidateQueries({ queryKey: ["contractors", slug] });
        return contractor!;
      }}
    />
  );
}

/**
 * The card a form sits in when it is part of the page. Inside a modal the
 * dialog is already the card, and a second border around the same fields reads
 * as a box in a box.
 */
const formCard = "mb-6 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm";

const FREQUENCIES = [
  ["monthly", "Monthly"],
  ["weekly", "Weekly"],
  ["biweekly", "Every 2 weeks"],
  ["quarterly", "Quarterly"],
  ["semiannual", "Every 6 months"],
  ["annual", "Annually"],
] as const;

/** A one-off expense, or the rule behind a recurring one. */
/** Prefilled values for editing an existing expense or recurring template. */
export interface ExpenseFormInitial {
  scope: "company" | "project";
  projectId: string | null;
  costCodeId: string;
  /** Description (one-off) or name (recurring). */
  text: string;
  amount: string;
  /** Incurred-on date (one-off) or anchor date (recurring). */
  date: string;
  frequency?: string | undefined;
}

export function ExpenseForm({
  recurring = false,
  currency,
  codes,
  projects,
  lockedProjectId,
  initial,
  bare = false,
  errors,
  formError,
  saving,
  submitLabel,
  onSubmit,
}: {
  recurring?: boolean | undefined;
  currency: string;
  codes: CodeOption[];
  projects: Option[];
  lockedProjectId?: string | undefined;
  /** Editing an existing row — prefills every field below. */
  initial?: ExpenseFormInitial | undefined;
  /** Drops the card chrome, for a form already inside a dialog. */
  bare?: boolean | undefined;
  errors: Record<string, string>;
  formError: string | null;
  saving: boolean;
  submitLabel: string;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
}) {
  const [scope, setScope] = useState<"company" | "project">(
    initial ? initial.scope : lockedProjectId ? "project" : "company",
  );
  const [projectId, setProjectId] = useState(initial?.projectId ?? lockedProjectId ?? "");
  const [costCodeId, setCostCodeId] = useState(initial?.costCodeId ?? "");
  const [text, setText] = useState(initial?.text ?? "");
  const [amount, setAmount] = useState(initial?.amount ?? "");
  const [date, setDate] = useState(initial?.date ?? todayIso());
  const [frequency, setFrequency] = useState<string>(initial?.frequency ?? "monthly");

  const onProject = !!lockedProjectId || scope === "project";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const base = {
      scope: onProject ? "project" : "company",
      // Sent as null, not omitted: the API's CHECK requires the absence to be
      // explicit for a company-scope expense.
      projectId: onProject ? (lockedProjectId ?? projectId) : null,
      costCodeId,
      amount,
      currency,
    };
    try {
      await onSubmit(
        recurring
          ? { ...base, name: text, frequency, anchorDate: date }
          : { ...base, description: text, incurredOn: date },
      );
      setText("");
      setAmount("");
    } catch {
      // Surfaced through formError / errors.
    }
  }

  return (
    <form onSubmit={submit} className={bare ? undefined : formCard}>
      {formError && <ErrorNote>{formError}</ErrorNote>}

      <div className="grid gap-3 sm:grid-cols-3">
        {!lockedProjectId && (
          <>
            <Field label="Belongs to" error={errors.projectId}>
              <select
                className={input}
                value={scope}
                onChange={(e) => setScope(e.target.value as "company" | "project")}
              >
                <option value="company">The company</option>
                <option value="project">A project</option>
              </select>
            </Field>

            {scope === "project" && (
              <Field label="Project" error={errors.projectId}>
                <select
                  className={input}
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  required
                >
                  <option value="">Pick a project…</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </>
        )}

        <Field label="Cost code" error={errors.costCodeId}>
          <select
            className={input}
            value={costCodeId}
            onChange={(e) => setCostCodeId(e.target.value)}
            required
          >
            <option value="">Pick a cost code…</option>
            {codes.map((cc) => (
              <option key={cc.id} value={cc.id}>
                {cc.code} — {cc.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label={recurring ? "Name" : "Description"} error={errors.name ?? errors.description}>
          <input
            className={input}
            value={text}
            onChange={(e) => setText(e.target.value)}
            required
          />
        </Field>

        <Field label={`Amount (${currency})`} error={errors.amount}>
          <input
            className={input}
            inputMode="decimal"
            placeholder="1500.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </Field>

        {recurring ? (
          <>
            <Field label="Repeats" error={errors.frequency}>
              <select
                className={input}
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
              >
                {FREQUENCIES.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Starting" hint="Also fixes the day it recurs" error={errors.anchorDate}>
              <input
                className={input}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </Field>
          </>
        ) : (
          <Field label="Date" error={errors.incurredOn}>
            <input
              className={input}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </Field>
        )}
      </div>

      <div className="mt-3">
        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}

/** Prefilled values for editing an existing commission. */
export interface CommissionFormInitial {
  scope: "company" | "project";
  projectId: string | null;
  clientId: string | null;
  contractorId: string | null;
  description: string;
  reference: string | null;
  amount: string;
  earnedOn: string;
}

export function CommissionForm({
  slug,
  currency,
  projects,
  clients,
  contractors,
  lockedProjectId,
  initial,
  bare = false,
  errors,
  formError,
  saving,
  submitLabel = "Record commission",
  onSubmit,
}: {
  slug: string;
  currency: string;
  projects: Option[];
  clients: Option[];
  contractors: Option[];
  lockedProjectId?: string | undefined;
  /** Editing an existing row — prefills every field below. */
  initial?: CommissionFormInitial | undefined;
  /** Drops the card chrome, for a form already inside a dialog. */
  bare?: boolean | undefined;
  errors: Record<string, string>;
  formError: string | null;
  saving: boolean;
  submitLabel?: string | undefined;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
}) {
  const qc = useQueryClient();
  const [scope, setScope] = useState<"company" | "project">(
    initial ? initial.scope : lockedProjectId ? "project" : "company",
  );
  const [projectId, setProjectId] = useState(initial?.projectId ?? lockedProjectId ?? "");
  // One picker for both party types: a commission comes from one party, and
  // two selects would let you name a client and a contractor at once.
  const [source, setSource] = useState(
    initial?.clientId
      ? `client:${initial.clientId}`
      : initial?.contractorId
        ? `contractor:${initial.contractorId}`
        : "",
  );
  const [description, setDescription] = useState(initial?.description ?? "");
  const [reference, setReference] = useState(initial?.reference ?? "");
  const [amount, setAmount] = useState(initial?.amount ?? "");
  const [date, setDate] = useState(initial?.earnedOn ?? todayIso());

  // Adding a new party from this picker takes a kind (which endpoint, which
  // optgroup it lands in) rather than reusing PickerWithAdd: one select here
  // stands for two resources, so "add new" needs to ask which one first.
  const [addingKind, setAddingKind] = useState<"client" | "contractor" | null>(null);
  const [newPartyName, setNewPartyName] = useState("");
  const [addingBusy, setAddingBusy] = useState(false);
  const [addingError, setAddingError] = useState<string | null>(null);

  function cancelAdding() {
    setAddingKind(null);
    setNewPartyName("");
    setAddingError(null);
  }

  async function addParty() {
    const trimmed = newPartyName.trim();
    if (!trimmed || !addingKind) return;
    setAddingBusy(true);
    setAddingError(null);
    try {
      if (addingKind === "client") {
        const { client } = await unwrap(
          await api.api.c[":slug"].clients.$post({ param: { slug }, json: { name: trimmed } }),
        );
        await qc.invalidateQueries({ queryKey: ["clients", slug] });
        // The insert always returns the row it just created — `!` documents
        // that this is a typing artifact of `.returning()`, not a real gap.
        setSource(`client:${client!.id}`);
      } else {
        const { contractor } = await unwrap(
          await api.api.c[":slug"].contractors.$post({
            param: { slug },
            json: { name: trimmed },
          }),
        );
        await qc.invalidateQueries({ queryKey: ["contractors", slug] });
        setSource(`contractor:${contractor!.id}`);
      }
      cancelAdding();
    } catch (err) {
      setAddingError(err instanceof ApiError ? err.message : "Couldn't add. Try again.");
    } finally {
      setAddingBusy(false);
    }
  }

  const onProject = !!lockedProjectId || scope === "project";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const [kind, id] = source ? source.split(":") : [];
    try {
      await onSubmit({
        scope: onProject ? "project" : "company",
        // Sent as null, not omitted: the API's CHECK requires the absence to be
        // explicit for a company-scope entry.
        projectId: onProject ? (lockedProjectId ?? projectId) : null,
        clientId: kind === "client" ? id : null,
        contractorId: kind === "contractor" ? id : null,
        description,
        ...(reference ? { reference } : {}),
        earnedOn: date,
        amount,
        currency,
      });
      setDescription("");
      setReference("");
      setAmount("");
    } catch {
      // Surfaced through formError / errors.
    }
  }

  return (
    <form onSubmit={submit} className={bare ? undefined : formCard}>
      {formError && <ErrorNote>{formError}</ErrorNote>}

      <div className="grid gap-3 sm:grid-cols-3">
        {!lockedProjectId && (
          <>
            <Field label="Belongs to" error={errors.projectId}>
              <select
                className={input}
                value={scope}
                onChange={(e) => setScope(e.target.value as "company" | "project")}
              >
                <option value="company">The company</option>
                <option value="project">A project</option>
              </select>
            </Field>

            {scope === "project" && (
              <Field label="Project" error={errors.projectId}>
                <select
                  className={input}
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  required
                >
                  <option value="">Pick a project…</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </>
        )}

        <Field
          label="From"
          hint={addingKind ? undefined : "Optional"}
          error={addingKind ? (addingError ?? undefined) : (errors.contractorId ?? errors.clientId)}
        >
          {addingKind ? (
            <div className="flex gap-2">
              <input
                className={input}
                value={newPartyName}
                onChange={(e) => setNewPartyName(e.target.value)}
                placeholder={addingKind === "client" ? "Client name" : "Contractor name"}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addParty();
                  }
                  if (e.key === "Escape") cancelAdding();
                }}
              />
              <Button onClick={addParty} disabled={addingBusy || !newPartyName.trim()}>
                {addingBusy ? "Adding…" : "Add"}
              </Button>
              <Button variant="ghost" onClick={cancelAdding}>
                Cancel
              </Button>
            </div>
          ) : (
            <select
              className={input}
              value={source}
              onChange={(e) => {
                if (e.target.value === "__new_client__") setAddingKind("client");
                else if (e.target.value === "__new_contractor__") setAddingKind("contractor");
                else setSource(e.target.value);
              }}
            >
              <option value="">Someone else</option>
              <optgroup label="Clients">
                {clients.map((cl) => (
                  <option key={cl.id} value={`client:${cl.id}`}>
                    {cl.name}
                  </option>
                ))}
                <option value="__new_client__">+ Add new client…</option>
              </optgroup>
              <optgroup label="Contractors">
                {contractors.map((ct) => (
                  <option key={ct.id} value={`contractor:${ct.id}`}>
                    {ct.name}
                  </option>
                ))}
                <option value="__new_contractor__">+ Add new contractor…</option>
              </optgroup>
            </select>
          )}
        </Field>

        <Field label="Description" error={errors.description}>
          <input
            className={input}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Referral fee, Harbour View"
            required
          />
        </Field>

        <Field label={`Amount (${currency})`} error={errors.amount}>
          <input
            className={input}
            inputMode="decimal"
            placeholder="2500.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </Field>

        <Field label="Earned on" error={errors.earnedOn}>
          <input
            className={input}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </Field>

        <Field label="Their reference" hint="Optional" error={errors.reference}>
          <input
            className={input}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="AGR-2026-11"
          />
        </Field>
      </div>

      <div className="mt-3">
        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}

export type CommitmentBasis = "fixed" | "percent_of_contract";

export interface CommitmentInput {
  projectId: string;
  contractorId: string;
  costCodeId: string;
  scope: string;
  basis: CommitmentBasis;
  contractValue?: string | undefined;
  percent?: string | undefined;
  changeOrderIds?: string[] | undefined;
  retainagePct: string;
  currency: string;
}

/**
 * What you've agreed to pay one contractor on one project.
 *
 * Two deals, one form. A fixed sum is a number you type. A share of the
 * contract is a percentage plus a decision about which change orders count —
 * taken here, at the moment the deal is written, because that is when anyone
 * knows the answer. Both can be revised later from the contractor's own row.
 */
export function CommitmentForm({
  slug,
  currency,
  projects,
  costCodes,
  lockedProjectId,
  lockedProjectValue,
  bare = false,
  busy,
  onSubmit,
}: {
  slug: string;
  currency: string;
  projects: ProjectOption[];
  costCodes: CodeOption[];
  lockedProjectId?: string | undefined;
  /** The locked project's contract value — `projects` is empty in that case. */
  lockedProjectValue?: string | undefined;
  /** Drops the card chrome, for a form already inside a dialog. */
  bare?: boolean | undefined;
  busy: boolean;
  onSubmit: (body: CommitmentInput) => void;
}) {
  const [projectId, setProjectId] = useState(lockedProjectId ?? "");
  const [contractorId, setContractorId] = useState("");
  const [costCodeId, setCostCodeId] = useState("");
  const [scope, setScope] = useState("");
  const [basis, setBasis] = useState<CommitmentBasis>("fixed");
  const [value, setValue] = useState("");
  const [percent, setPercent] = useState("6");
  const [retainage, setRetainage] = useState("0");
  const [attached, setAttached] = useState<Set<string>>(new Set());

  const chosenProject = lockedProjectId ?? projectId;
  const share = basis === "percent_of_contract";

  // Only a share cares what change orders exist, and only once a project is
  // picked — there is nothing to list before that.
  const changeOrders = useQuery({
    queryKey: ["change-orders", slug, chosenProject],
    enabled: share && !!chosenProject,
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].billing[":projectId"]["change-orders"].$get({
          param: { slug, projectId: chosenProject },
        }),
      ),
  });

  const offered = (changeOrders.data?.changeOrders ?? []).filter((co) => co.status !== "void");

  // What the percent field resolves to, shown live rather than left to be
  // discovered after saving — the same base `resolveCommitment` uses: the
  // project's contract plus whichever approved change orders are ticked.
  const percentBase = lockedProjectId
    ? lockedProjectValue
    : projects.find((p) => p.id === chosenProject)?.originalContractValue;
  const countedForBase = offered.filter((co) => co.status === "approved" && attached.has(co.id));
  const shareTotal =
    share && percentBase
      ? mul(
          sum([percentBase, ...countedForBase.map((co) => co.amount)]),
          rateFromPercent(percent || "0"),
        )
      : null;

  return (
    <form
      className={bare ? undefined : formCard}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          projectId: chosenProject,
          contractorId,
          costCodeId,
          scope,
          basis,
          ...(share
            ? {
                percent: rateFromPercent(percent),
                changeOrderIds: [...attached],
              }
            : { contractValue: value }),
          retainagePct: rateFromPercent(retainage),
          currency,
        });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        {!lockedProjectId && (
          <Field label="Project">
            <select
              className={input}
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value);
                // The old project's change orders have nothing to do with the
                // new one, and carrying the ticks over would attach the wrong
                // documents to the share.
                setAttached(new Set());
              }}
              required
            >
              <option value="">Pick a project…</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <ContractorPicker slug={slug} value={contractorId} onChange={setContractorId} required />
        <Field label="Cost code" hint="Bills must be coded">
          <select
            className={input}
            value={costCodeId}
            onChange={(e) => setCostCodeId(e.target.value)}
            required
          >
            <option value="">Pick a cost code…</option>
            {costCodes.map((cc) => (
              <option key={cc.id} value={cc.id}>
                {cc.code} — {cc.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Scope">
          <input
            className={input}
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            placeholder="Framing package"
          />
        </Field>
        <Field label="Paid" hint={share ? "Moves with the contract" : "A number you agreed"}>
          <select
            className={input}
            value={basis}
            onChange={(e) => setBasis(e.target.value as CommitmentBasis)}
          >
            <option value="fixed">A fixed sum</option>
            <option value="percent_of_contract">A share of the contract</option>
          </select>
        </Field>
        {share ? (
          <Field
            label="Share of the contract"
            hint={
              shareTotal
                ? `${fmtAmount(shareTotal)} of ${fmtAmount(percentBase!)}`
                : "Of the project total, plus what you tick below"
            }
          >
            <PercentInput value={percent} onChange={setPercent} required />
          </Field>
        ) : (
          <Field label={`Value (${currency})`}>
            <input
              className={input}
              inputMode="decimal"
              placeholder="60000.00"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
            />
          </Field>
        )}
        <Field label="Retainage" hint="Held back from each bill">
          <PercentInput value={retainage} onChange={setRetainage} />
        </Field>
      </div>

      {share && chosenProject && (
        <ChangeOrderPicker
          rows={offered}
          pending={changeOrders.isPending}
          attached={attached}
          onToggle={setAttached}
        />
      )}

      <div className="mt-3">
        <Button type="submit" disabled={busy}>
          Create commitment
        </Button>
      </div>
    </form>
  );
}

/**
 * Which change orders a share is taken of.
 *
 * Ticked one at a time, with "all" and "none" as shortcuts rather than as
 * stored rules — the record is always the list, so a contractor's cut can be
 * explained by pointing at the documents inside it. An unapproved change order
 * can be ticked and simply counts for nothing until it is approved, which is
 * the same thing it does to the client's contract.
 */
export function ChangeOrderPicker({
  rows,
  pending,
  attached,
  onToggle,
  busy,
  onSave,
}: {
  rows: {
    id: string;
    number: number;
    title: string;
    amount: string;
    status: string;
  }[];
  pending: boolean;
  attached: Set<string>;
  onToggle: (next: Set<string>) => void;
  busy?: boolean | undefined;
  onSave?: (() => void) | undefined;
}) {
  const counted = rows.filter((co) => co.status === "approved" && attached.has(co.id));

  return (
    <div className="mt-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
          Change orders in this share
        </span>
        {rows.length > 0 && (
          <span className="flex gap-2">
            <Button variant="ghost" onClick={() => onToggle(new Set(rows.map((co) => co.id)))}>
              All
            </Button>
            <Button variant="ghost" onClick={() => onToggle(new Set())}>
              None
            </Button>
          </span>
        )}
      </div>

      {pending ? (
        <Loading />
      ) : rows.length === 0 ? (
        <p className="text-sm text-neutral-500">
          No change orders on this project yet. Any raised later can be added to this share from the
          contractor's row.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {rows.map((co) => (
            <li key={co.id}>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={attached.has(co.id)}
                  onChange={() => {
                    const next = new Set(attached);
                    if (next.has(co.id)) next.delete(co.id);
                    else next.add(co.id);
                    onToggle(next);
                  }}
                />
                <span className="tabular text-neutral-500">CO {co.number}</span>
                <span className="flex-1">{co.title}</span>
                {co.status !== "approved" && (
                  <span className="rounded-full border border-neutral-200 bg-white px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-neutral-500">
                    {co.status} — counts for nothing yet
                  </span>
                )}
                <span className="tabular text-neutral-600">{fmtAmount(co.amount)}</span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {rows.length > 0 && (
        <p className="mt-2 text-xs text-neutral-500">
          {counted.length === 0
            ? "Nothing ticked and approved — the share is of the original contract alone."
            : `${counted.length} approved change order${counted.length === 1 ? "" : "s"} adding ${fmtAmount(
                sum(counted.map((co) => co.amount)),
              )} to the base.`}
        </p>
      )}

      {onSave && (
        <div className="mt-3">
          <Button type="button" disabled={busy} onClick={onSave}>
            Save selection
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * How a contractor's money was arrived at, and the two levers on it.
 *
 * A share of the contract is not a number anyone typed, so this shows the
 * arithmetic rather than the answer: the project's contract, the change orders
 * folded in, the percentage, and what has been charged back. Both levers live
 * here because they are the same question asked twice — is this the right base,
 * and is this the right amount to come off it.
 *
 * Charge-backs apply to a fixed commitment too. A contractor who broke
 * something owes for it whether their deal was a percentage or a lump sum.
 */
/** What the "Owed" figure is net of, in plain words — charged-back, paid, both, or neither. */
function owedHint(deductionTotal: string, paid: string, currency: string): string | undefined {
  const parts: string[] = [];
  if (deductionTotal !== "0.00") parts.push(`${fmtAmount(deductionTotal, currency)} charged back`);
  if (paid !== "0.00") parts.push(`${fmtAmount(paid, currency)} already paid`);
  return parts.length ? `Net of ${parts.join(" and ")}` : "Nothing charged back or paid yet";
}

export function CommitmentEarnings({
  slug,
  commitmentId,
  contractorName,
  busy,
  onError,
  onChanged,
}: {
  slug: string;
  commitmentId: string;
  /** From the row that opened this panel — the earnings response has no join to name. */
  contractorName?: string | undefined;
  busy: boolean;
  onError: (message: string | null) => void;
  onChanged: () => void;
}) {
  const [attached, setAttached] = useState<Set<string> | null>(null);
  const [showDeduction, setShowDeduction] = useState(false);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [appliedOn, setAppliedOn] = useState(todayIso());
  /** The expense this charge-back recovers, or "" for one that isn't on the books. */
  const [expenseId, setExpenseId] = useState("");

  const earnings = useQuery({
    queryKey: ["commitment-earnings", slug, commitmentId],
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].payables.commitments[":id"].earnings.$get({
          param: { slug, id: commitmentId },
        }),
      ),
  });

  const run = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onMutate: () => onError(null),
    onSuccess: () => {
      earnings.refetch();
      onChanged();
    },
    onError: (e) => onError(e instanceof ApiError ? e.message : "Something went wrong. Try again."),
  });

  if (earnings.isPending) return <Loading />;
  if (earnings.isError) return <ErrorNote>{(earnings.error as Error).message}</ErrorNote>;

  const {
    commitment,
    resolved,
    paid,
    originalContractValue,
    changeOrders,
    deductions,
    recoverableExpenses,
  } = earnings.data;
  const share = commitment.basis === "percent_of_contract";
  const ccy = commitment.currency;
  // What's left once payments already made against this commitment's bills
  // come off — the resolved share is the whole target, not the balance.
  const remaining = sub(resolved.value, paid);

  // Untouched, the picker shows what is stored; once touched it shows the
  // edit in progress, which is what "Save selection" then commits.
  const selection =
    attached ?? new Set(changeOrders.filter((co) => co.attached).map((co) => co.id));
  const dirty = attached !== null;

  const selectedExpense = recoverableExpenses.find((x) => x.id === expenseId);

  return (
    <div className="mt-6 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
      <h3 className="mb-3 text-sm font-medium">
        What this contractor earns
        {contractorName && (
          <span className="font-normal text-neutral-500"> — {contractorName}</span>
        )}
      </h3>

      {share ? (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-4">
            <Stat
              label="Project contract"
              value={fmtAmount(originalContractValue)}
              currency={ccy}
            />
            <Stat
              label="Change orders in"
              value={fmtAmount(resolved.changeOrderTotal)}
              currency={ccy}
              hint="Approved and ticked below"
            />
            <Stat
              label={`Share at ${formatPercent(commitment.percent ?? "0")}`}
              value={fmtAmount(resolved.gross)}
              currency={ccy}
              hint={`of ${fmtAmount(resolved.percentBase)}`}
            />
            <Stat
              label="Owed"
              value={fmtAmount(remaining)}
              currency={ccy}
              hint={owedHint(resolved.deductionTotal, paid, ccy)}
              emphasis
            />
          </div>

          <ChangeOrderPicker
            rows={changeOrders}
            pending={false}
            attached={selection}
            onToggle={setAttached}
            busy={busy || run.isPending}
            {...(dirty
              ? {
                  onSave: () =>
                    run.mutate(async () => {
                      await unwrap(
                        api.api.c[":slug"].payables.commitments[":id"]["change-orders"].$put({
                          param: { slug, id: commitmentId },
                          json: { changeOrderIds: [...selection] },
                        }),
                      );
                      setAttached(null);
                    }),
                }
              : {})}
          />
        </>
      ) : (
        <div className="mb-4 grid gap-3 sm:grid-cols-4">
          <Stat label="Agreed" value={fmtAmount(resolved.gross)} currency={ccy} />
          <Stat label="Charged back" value={fmtAmount(resolved.deductionTotal)} currency={ccy} />
          <Stat label="Paid" value={fmtAmount(paid)} currency={ccy} />
          <Stat
            label="Owed"
            value={fmtAmount(remaining)}
            currency={ccy}
            hint={owedHint(resolved.deductionTotal, paid, ccy)}
            emphasis
          />
        </div>
      )}

      {resolved.unappliedDeduction !== "0.00" && (
        <p className="mt-3 text-xs text-amber-700">
          {fmtAmount(resolved.unappliedDeduction, ccy)} of charge-backs is more than this commitment
          is worth. It is owed to you some other way — the commitment stops at zero rather than
          going negative.
        </p>
      )}

      <div className="mt-5 flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
          Charged back
        </span>
        <Button
          variant="ghost"
          onClick={() => {
            setShowDeduction(true);
            setExpenseId("");
            onError(null);
          }}
        >
          Charge something back
        </Button>
      </div>

      {showDeduction && (
        <Modal title="Charge something back" wide onClose={() => setShowDeduction(false)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              run.mutate(async () => {
                await unwrap(
                  api.api.c[":slug"].payables.commitments[":id"].deductions.$post({
                    param: { slug, id: commitmentId },
                    json: {
                      description: description.trim(),
                      amount,
                      appliedOn,
                      // Omitted rather than sent as null when nothing was
                      // picked, so a charge-back that recovers nothing on the
                      // books stays exactly the request it always was.
                      ...(expenseId ? { expenseId } : {}),
                    },
                  }),
                );
                setDescription("");
                setAmount("");
                setExpenseId("");
                setShowDeduction(false);
              });
            }}
          >
            {recoverableExpenses.length > 0 && (
              <div className="mb-3">
                <Field
                  label="Recover a recorded cost"
                  hint="Optional — ties this to the expense it takes back"
                >
                  <select
                    className={input}
                    value={expenseId}
                    onChange={(e) => {
                      const next = e.target.value;
                      setExpenseId(next);
                      const hit = recoverableExpenses.find((x) => x.id === next);
                      // Prefilled from the expense, because retyping a figure
                      // already on the books is how the two come to disagree.
                      // Both stay editable: a contractor can be on the hook
                      // for part of a cost rather than all of it.
                      if (hit) {
                        setDescription(hit.description);
                        setAmount(hit.amount);
                      }
                    }}
                  >
                    <option value="">Not a recorded cost — describe it below</option>
                    {recoverableExpenses.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.incurredOn} · {x.costCode} · {fmtAmount(x.amount, ccy)} — {x.description}
                        {x.recoveredElsewhere ? " (already recovered elsewhere)" : ""}
                      </option>
                    ))}
                  </select>
                </Field>
                {selectedExpense?.recoveredElsewhere && (
                  <p className="mt-2 max-w-prose text-xs text-amber-700">
                    This cost is already charged back against another commitment. Recovering it here
                    as well takes it back twice — which is right only if two contractors are
                    genuinely splitting it, and then each share should be less than the whole.
                  </p>
                )}
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="What for">
                <input
                  className={input}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Replaced the glazing they broke"
                  required
                />
              </Field>
              <Field label={`Amount (${ccy})`}>
                <input
                  className={input}
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="750.00"
                  required
                />
              </Field>
              <Field label="Applied on">
                <input
                  className={input}
                  type="date"
                  value={appliedOn}
                  onChange={(e) => setAppliedOn(e.target.value)}
                  required
                />
              </Field>
            </div>
            <p className="mt-2 max-w-prose text-xs text-neutral-500">
              This comes off what the commitment is worth, so the draws still to come re-resolve
              around it — the contractor's total lands where it should however many payments are
              left.
            </p>
            <div className="mt-3">
              <Button type="submit" disabled={run.isPending}>
                Charge it back
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {deductions.length === 0 ? (
        <p className="mt-2 text-sm text-neutral-500">Nothing charged back.</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1.5">
          {deductions.map((d) => (
            <li key={d.id} className="flex items-center gap-2 text-sm">
              <span className="tabular text-neutral-400">{d.appliedOn}</span>
              <span className={`flex-1 ${d.status === "void" ? "text-neutral-400" : ""}`}>
                <span className={d.status === "void" ? "line-through" : ""}>{d.description}</span>
                {/* Which recorded cost this takes back, when it takes one
                    back. Without it the link is invisible and the only thing
                    tying the two together is whatever someone typed. */}
                {d.expenseDescription && (
                  <span className="block text-xs text-neutral-500">
                    Recovers {d.expenseDescription} ({d.expenseIncurredOn}
                    {/* The expense's own figure, but only when it differs —
                        a partial recovery, where this contractor carries
                        some of a cost rather than all of it. */}
                    {d.expenseAmount !== null &&
                      d.expenseAmount !== d.amount &&
                      `, cost was ${fmtAmount(d.expenseAmount, ccy)}`}
                    )
                  </span>
                )}
              </span>
              <span className="tabular">{fmtAmount(d.amount)}</span>
              {d.status === "void" ? (
                <span className="text-xs text-neutral-400">voided</span>
              ) : (
                <Button
                  variant="ghost"
                  disabled={run.isPending}
                  onClick={() =>
                    run.mutate(() =>
                      unwrap(
                        api.api.c[":slug"].payables.commitments[":id"].deductions[
                          ":deductionId"
                        ].void.$post({
                          param: { slug, id: commitmentId, deductionId: d.id },
                        }),
                      ),
                    )
                  }
                >
                  Void
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * A percentage split of a contract into the payments it will be settled in.
 *
 * The same form on both sides of the ledger: a project's milestones are what
 * the client will be invoiced, a commitment's draws are what a contractor will
 * bill — and `resolveSchedule` treats them identically.
 */
export function ScheduleRowsForm({
  intro,
  addLabel,
  submitLabel,
  defaultRows,
  busy,
  onSubmit,
}: {
  intro: string;
  addLabel: string;
  submitLabel: string;
  /** Percentages as people write them: `50`, not `0.50`. */
  defaultRows: { name: string; percent: string }[];
  busy: boolean;
  onSubmit: (rows: { name: string; basis: "percent"; percent: string }[]) => void;
}) {
  // Keyed by identity rather than index: removing the second of three rows
  // must take that row's inputs with it, not shuffle the third one's text up.
  const nextKey = useRef(0);
  const [rows, setRows] = useState(() =>
    defaultRows.map((r) => ({ ...r, key: nextKey.current++ })),
  );

  // Shown, not enforced. A schedule that doesn't reach 100% is legal — the
  // remainder is reported as unallocated rather than refused — but typing
  // three numbers and not being told they come to 90 is how it happens by
  // accident.
  const total = rows.reduce((acc, r) => acc + (Number(r.percent) || 0), 0);
  const rounded = Math.round(total * 10000) / 10000;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(
          rows.map((r) => ({
            name: r.name,
            basis: "percent" as const,
            percent: rateFromPercent(r.percent),
          })),
        );
      }}
    >
      <p className="mb-3 max-w-prose text-sm text-neutral-500">{intro}</p>
      <div className="mb-3 flex flex-col gap-2">
        {rows.map((r, i) => (
          <div key={r.key} className="flex gap-2">
            <input
              className={input}
              value={r.name}
              onChange={(e) =>
                setRows(rows.map((x, j) => (i === j ? { ...x, name: e.target.value } : x)))
              }
              required
            />
            <div className="w-32 shrink-0">
              <PercentInput
                value={r.percent}
                onChange={(next) =>
                  setRows(rows.map((x, j) => (i === j ? { ...x, percent: next } : x)))
                }
                required
              />
            </div>
            <Button variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
              Remove
            </Button>
          </div>
        ))}
      </div>
      <p className={`mb-3 text-xs ${rounded === 100 ? "text-neutral-500" : "text-amber-700"}`}>
        {rounded === 100
          ? "Adds up to 100%."
          : `Adds up to ${rounded}% — ${rounded < 100 ? `${Math.round((100 - rounded) * 10000) / 10000}% of the total has nothing to bill it against` : "this schedule bills more than the total"}.`}
      </p>
      <div className="flex gap-2">
        <Button
          variant="ghost"
          onClick={() => setRows([...rows, { name: "", percent: "0", key: nextKey.current++ }])}
        >
          {addLabel}
        </Button>
        <Button type="submit" disabled={busy}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

const PAYMENT_METHODS = [
  ["bank_transfer", "Bank transfer"],
  ["check", "Check"],
  ["card", "Card"],
  ["cash", "Cash"],
  ["other", "Other"],
] as const;

export interface InvoiceRow {
  id: string;
  number: string;
  status: string;
  issueDate: string;
  dueDate: string;
  retainageWithheld: string;
  total: string;
  amountPaid: string;
  /** Derived server-side, so the browser never subtracts money. */
  outstanding: string;
  currency: string;
  clientId: string;
  overdue: boolean;
}

/** Narrowed to the API's enum, so a typo here is a compile error, not a 422. */
type PaymentMethod = (typeof PAYMENT_METHODS)[number][0];

export interface PaymentInput {
  clientId: string;
  receivedOn: string;
  amount: string;
  currency: string;
  method?: PaymentMethod | undefined;
  reference?: string | undefined;
  applications: { invoiceId: string; amount: string }[];
}

/**
 * The document that settles one scheduled line: an invoice to a client, or a
 * bill from a contractor. Mirrors the `Settlement` the API builds — the same
 * shape on both sides of the money, so one form settles either.
 */
export interface Settlement {
  id: string;
  number: string | null;
  status: string;
  dueDate: string;
  total: string;
  amountPaid: string;
  outstanding: string;
  overdue: boolean;
  /** The client on an invoice, the contractor on a bill. */
  partyId: string;
}

/**
 * What the form collects. Field *names* differ between the two endpoints —
 * `receivedOn` versus `paidOn`, `invoiceId` versus `billId` — so the caller
 * maps this onto its own request rather than the form guessing which
 * direction the money is going.
 */
export interface SettlementPayment {
  amount: string;
  on: string;
  method?: PaymentMethod | undefined;
  reference?: string | undefined;
}

/**
 * Invoices, and settling them.
 *
 * Payment is applied per invoice rather than as one lump: the API models a
 * payment as a set of applications precisely because one cheque can settle
 * three invoices and one invoice can take three cheques, and it is the
 * application — not the payment — that moves the ledger. Paying in full and
 * paying part of it are therefore the same action with a different number in
 * it, which is why there is no separate "mark as paid" button.
 */
export function Invoices({
  rows,
  pending,
  busy,
  onRecordPayment,
  onDelete,
}: {
  rows: InvoiceRow[];
  pending: boolean;
  busy: boolean;
  onRecordPayment: (body: PaymentInput) => void;
  /** Passed by owners only — the caller decides who is allowed to see it. */
  onDelete?: ((id: string) => void) | undefined;
}) {
  const [payingId, setPayingId] = useState<string | null>(null);

  const table = useTable(rows, {
    filter: (i, q) => `${i.number} ${i.status}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      number: (i) => i.number,
      issued: (i) => i.issueDate,
      due: (i) => i.dueDate,
      status: (i) => i.status,
      retainage: (i) => Number(i.retainageWithheld),
      total: (i) => Number(i.total),
      paid: (i) => Number(i.amountPaid),
      outstanding: (i) => Number(i.outstanding),
    },
  });

  if (pending) {
    return (
      <Panel title="Invoices">
        <Loading />
      </Panel>
    );
  }

  if (rows.length === 0) {
    return (
      <Panel title="Invoices">
        <Empty title="Nothing invoiced yet">Invoice a milestone to start.</Empty>
      </Panel>
    );
  }

  return (
    <Panel title="Invoices">
      <TableSearch value={table.query} onChange={table.setQuery} placeholder="Filter invoices…" />
      <Table
        head={
          <>
            <SortTh table={table} sortKey="number">
              Number
            </SortTh>
            <SortTh table={table} sortKey="issued">
              Issued
            </SortTh>
            <SortTh table={table} sortKey="due">
              Due
            </SortTh>
            <SortTh table={table} sortKey="status">
              Status
            </SortTh>
            <SortTh table={table} sortKey="retainage" right>
              Retainage
            </SortTh>
            <SortTh table={table} sortKey="total" right>
              Total
            </SortTh>
            <SortTh table={table} sortKey="paid" right>
              Paid
            </SortTh>
            <SortTh table={table} sortKey="outstanding" right>
              Outstanding
            </SortTh>
            <Th />
          </>
        }
      >
        {table.rows.map((i) => (
          <Fragment key={i.id}>
            <tr className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100">
              <Td mono>{i.number}</Td>
              <Td mono muted>
                {i.issueDate}
              </Td>
              <Td mono muted>
                {i.dueDate}
              </Td>
              <Td>
                {i.status.replace(/_/g, " ")}
                {i.overdue && <span className="ml-2 text-xs text-red-600">overdue</span>}
              </Td>
              <Td right mono muted>
                {fmtAmount(i.retainageWithheld)}
              </Td>
              <Td right mono>
                {fmtAmount(i.total)}
              </Td>
              <Td right mono muted>
                {fmtAmount(i.amountPaid)}
              </Td>
              <Td right mono>
                {fmtAmount(i.outstanding)}
              </Td>
              <Td right>
                <div className="flex justify-end gap-1">
                  {/* Nothing left to settle, or nothing that should be. */}
                  {i.outstanding !== "0.00" && i.status !== "void" && (
                    <Button
                      variant="ghost"
                      onClick={() => setPayingId(payingId === i.id ? null : i.id)}
                    >
                      {payingId === i.id ? "Cancel" : "Record payment"}
                    </Button>
                  )}
                  {onDelete && (
                    <DeleteButton
                      busy={busy}
                      onConfirm={() => {
                        setPayingId(null);
                        onDelete(i.id);
                      }}
                    />
                  )}
                </div>
              </Td>
            </tr>
            {payingId === i.id && (
              <tr>
                <td colSpan={9} className="bg-neutral-50 px-4 py-4">
                  <RecordPaymentForm
                    settlement={i}
                    currency={i.currency}
                    noun="invoice"
                    dateLabel="Received on"
                    busy={busy}
                    onSubmit={(p) => {
                      onRecordPayment({
                        clientId: i.clientId,
                        receivedOn: p.on,
                        amount: p.amount,
                        currency: i.currency,
                        ...(p.method ? { method: p.method } : {}),
                        ...(p.reference ? { reference: p.reference } : {}),
                        applications: [{ invoiceId: i.id, amount: p.amount }],
                      });
                      setPayingId(null);
                    }}
                  />
                </td>
              </tr>
            )}
          </Fragment>
        ))}
      </Table>
    </Panel>
  );
}

/**
 * The amount defaults to everything outstanding, so settling in full is the
 * path of least resistance and a partial payment is an edit rather than a
 * different form. Over-application is left to the API, which knows the real
 * balance and says so exactly — a second opinion computed in the browser could
 * only ever be a staler one.
 */
export function RecordPaymentForm({
  settlement,
  currency,
  noun,
  dateLabel,
  busy,
  onSubmit,
}: {
  settlement: Pick<Settlement, "number" | "outstanding">;
  currency: string;
  /** "invoice" or "bill" — copy only. */
  noun: string;
  /** "Received on" for money in, "Paid on" for money out. */
  dateLabel: string;
  busy: boolean;
  onSubmit: (payment: SettlementPayment) => void;
}) {
  const [amount, setAmount] = useState(settlement.outstanding);
  const [on, setOn] = useState(todayIso());
  const [method, setMethod] = useState<PaymentMethod | "">("");
  const [reference, setReference] = useState("");

  const partial = amount !== settlement.outstanding;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          amount,
          on,
          ...(method ? { method } : {}),
          ...(reference ? { reference } : {}),
        });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-4">
        <Field
          label={`Amount (${currency})`}
          hint={
            partial
              ? `Leaves part of ${fmtAmount(settlement.outstanding)} outstanding`
              : `Settles ${settlement.number ?? `this ${noun}`} in full`
          }
        >
          <input
            className={input}
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </Field>

        <Field label={dateLabel}>
          <input
            className={input}
            type="date"
            value={on}
            onChange={(e) => setOn(e.target.value)}
            required
          />
        </Field>

        <Field label="Method" hint="Optional">
          <select
            className={input}
            value={method}
            onChange={(e) => setMethod(e.target.value as PaymentMethod | "")}
          >
            <option value="">Not recorded</option>
            {PAYMENT_METHODS.map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Reference" hint="Optional">
          <input
            className={input}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Cheque no., transfer id"
          />
        </Field>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <Button type="submit" disabled={busy}>
          {busy ? "Recording…" : partial ? "Record part payment" : "Record payment"}
        </Button>
        <Button variant="ghost" onClick={() => setAmount(settlement.outstanding)}>
          Pay in full
        </Button>
      </div>
    </form>
  );
}

/**
 * The individual payments behind one settlement's paid total.
 *
 * `amountPaid` on a bill or invoice is the number that matters for the running
 * balance, but "who paid what, and when" is a different question — one wire
 * can settle a bill in three installments, and the total alone can't answer
 * which. This is that answer, fetched only once the row is actually opened.
 */
export function PaymentsList({
  slug,
  kind,
  id,
  currency,
}: {
  slug: string;
  kind: "bill" | "invoice";
  id: string;
  currency: string;
}) {
  const payments = useQuery({
    queryKey: ["settlement-payments", kind, slug, id],
    queryFn: async () => {
      if (kind === "bill") {
        const { payments } = await unwrap(
          api.api.c[":slug"].payables.bills[":id"].payments.$get({ param: { slug, id } }),
        );
        return payments.map((p) => ({
          id: p.id,
          on: p.paidOn,
          amount: p.amount,
          method: p.method,
          reference: p.reference,
        }));
      }
      const { payments } = await unwrap(
        api.api.c[":slug"].invoices[":id"].payments.$get({ param: { slug, id } }),
      );
      return payments.map((p) => ({
        id: p.id,
        on: p.receivedOn,
        amount: p.amount,
        method: p.method,
        reference: p.reference,
      }));
    },
  });

  if (payments.isPending) return <Loading />;
  if (payments.isError) return <ErrorNote>{(payments.error as Error).message}</ErrorNote>;
  if (payments.data.length === 0) {
    return <p className="px-1 py-1.5 text-sm text-neutral-500">No payments recorded yet.</p>;
  }

  return (
    <table className="w-full text-sm">
      <tbody>
        {payments.data.map((p) => (
          <tr key={p.id} className="border-b border-neutral-100 last:border-0">
            <td className="py-1.5 pr-4 font-mono text-neutral-500">{p.on}</td>
            <td className="py-1.5 pr-4 font-mono">{fmtAmount(p.amount, currency)}</td>
            <td className="py-1.5 pr-4 text-neutral-500">
              {p.method ? p.method.replace(/_/g, " ") : "—"}
            </td>
            <td className="py-1.5 text-neutral-500">{p.reference ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * A change order's status, told from the invoice rather than the column.
 *
 * The stored status stays "approved" once billed — being on an invoice is a
 * fact about `invoice_lines`, not something written back to the change order —
 * so "invoiced" and "paid" are read off the settlement.
 */
export function ChangeOrderStatus({
  status,
  settlement,
}: {
  status: string;
  settlement?: Settlement | undefined;
}) {
  if (!settlement) {
    return <span className={status === "approved" ? "" : "text-neutral-500"}>{status}</span>;
  }
  const paid = settlement.outstanding === "0.00";
  return (
    <span className={paid ? "text-green-700" : "text-blue-700"}>{paid ? "paid" : "invoiced"}</span>
  );
}

/**
 * The one thing left to do to a change order, in the row it sits on: invoice
 * it, then settle it. The same progression a milestone goes through, because
 * it is the same progression.
 */
export function ChangeOrderAction({
  settlement,
  canInvoice,
  busy,
  payOpen,
  onInvoice,
  onTogglePay,
}: {
  settlement?: Settlement | undefined;
  /** Approved, unbilled, and therefore invoiceable on its own right now. */
  canInvoice: boolean;
  busy: boolean;
  payOpen: boolean;
  onInvoice: () => void;
  onTogglePay: () => void;
}) {
  if (settlement) {
    if (settlement.outstanding === "0.00") return null;
    return (
      <Button variant="ghost" onClick={onTogglePay}>
        {payOpen ? "Cancel" : "Record payment"}
      </Button>
    );
  }
  if (!canInvoice) return null;
  return (
    <Button variant="ghost" disabled={busy} onClick={onInvoice}>
      Invoice
    </Button>
  );
}

export type BillingMode = "next_invoice" | "own_milestone" | "spread_remaining";

export interface ChangeOrderRow {
  id: string;
  number: number;
  title: string;
  amount: string;
  status: string;
  billingMode: string;
}

export function ChangeOrders({
  rows,
  pending,
  busy,
  currency,
  settlements = {},
  pendingIds = [],
  onCreate,
  onApprove,
  onInvoice,
  onRecordPayment,
  onDelete,
}: {
  rows: ChangeOrderRow[];
  pending: boolean;
  busy: boolean;
  currency: string;
  /** Passed by owners only — the caller decides who is allowed to see it. */
  onDelete?: ((id: string) => void) | undefined;
  /** Change order → the invoice carrying it, for the ones already billed. */
  settlements?: Record<string, Settlement> | undefined;
  /** Approved and unbilled, so invoiceable on their own. */
  pendingIds?: readonly string[] | undefined;
  onCreate: (body: { title: string; amount: string; billingMode: BillingMode }) => void;
  onApprove: (id: string) => void;
  onInvoice?: ((id: string) => void) | undefined;
  onRecordPayment?: ((settlement: Settlement, payment: SettlementPayment) => void) | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState<BillingMode>("next_invoice");

  const changeOrdersTable = useTable(rows, {
    filter: (co, q) =>
      `${co.title} ${co.billingMode} ${co.status}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      number: (co) => co.number,
      title: (co) => co.title.toLowerCase(),
      billsAs: (co) => co.billingMode,
      status: (co) => co.status,
      amount: (co) => Number(co.amount),
    },
  });

  return (
    <Panel
      title="Change orders"
      action={<Button onClick={() => setOpen(true)}>New change order</Button>}
    >
      <p className="mb-3 max-w-prose text-sm text-neutral-500">
        Add-ons and extra work between scheduled payments. By default they ride along on the next
        invoice rather than becoming a separate document.
      </p>

      {open && (
        <Modal title="New change order" wide onClose={() => setOpen(false)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onCreate({ title, amount, billingMode: mode });
              setTitle("");
              setAmount("");
              setOpen(false);
            }}
          >
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Title">
                <input
                  className={input}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </Field>
              <Field label="Amount" hint="Negative for removed scope">
                <input
                  className={input}
                  inputMode="decimal"
                  placeholder="4200.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </Field>
              <Field label="Bill it">
                <select
                  className={input}
                  value={mode}
                  onChange={(e) => setMode(e.target.value as BillingMode)}
                >
                  <option value="next_invoice">On the next invoice</option>
                  <option value="own_milestone">As its own milestone</option>
                  <option value="spread_remaining">Spread across remaining</option>
                </select>
              </Field>
            </div>
            <div className="mt-3">
              <Button type="submit" disabled={busy}>
                Create
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {pending ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty title="No change orders" />
      ) : (
        <>
          <TableSearch
            value={changeOrdersTable.query}
            onChange={changeOrdersTable.setQuery}
            placeholder="Filter change orders…"
          />
          <Table
            head={
              <>
                <Th
                  sortKey="number"
                  active={changeOrdersTable.sortKey === "number"}
                  dir={changeOrdersTable.dir}
                  onSort={changeOrdersTable.toggleSort}
                >
                  #
                </Th>
                <Th
                  sortKey="title"
                  active={changeOrdersTable.sortKey === "title"}
                  dir={changeOrdersTable.dir}
                  onSort={changeOrdersTable.toggleSort}
                >
                  Title
                </Th>
                <Th
                  sortKey="billsAs"
                  active={changeOrdersTable.sortKey === "billsAs"}
                  dir={changeOrdersTable.dir}
                  onSort={changeOrdersTable.toggleSort}
                >
                  Bills as
                </Th>
                <Th
                  sortKey="status"
                  active={changeOrdersTable.sortKey === "status"}
                  dir={changeOrdersTable.dir}
                  onSort={changeOrdersTable.toggleSort}
                >
                  Status
                </Th>
                <Th
                  right
                  sortKey="amount"
                  active={changeOrdersTable.sortKey === "amount"}
                  dir={changeOrdersTable.dir}
                  onSort={changeOrdersTable.toggleSort}
                >
                  Amount
                </Th>
                <Th>Invoice</Th>
                <Th right>Outstanding</Th>
                <Th />
              </>
            }
          >
            {changeOrdersTable.rows.map((co) => (
              <Fragment key={co.id}>
                <tr className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100">
                  <Td mono muted>
                    {co.number}
                  </Td>
                  <Td>{co.title}</Td>
                  <Td muted>{co.billingMode.replace(/_/g, " ")}</Td>
                  <Td>
                    <ChangeOrderStatus status={co.status} settlement={settlements[co.id]} />
                  </Td>
                  <Td right mono>
                    {fmtAmount(co.amount)}
                  </Td>
                  <Td mono muted>
                    {settlements[co.id]?.number ?? "—"}
                  </Td>
                  <Td right mono>
                    {settlements[co.id] ? fmtAmount(settlements[co.id]!.outstanding) : "—"}
                  </Td>
                  <Td right>
                    <div className="flex justify-end gap-1">
                      {co.status !== "approved" ? (
                        <Button variant="ghost" disabled={busy} onClick={() => onApprove(co.id)}>
                          Approve
                        </Button>
                      ) : (
                        <ChangeOrderAction
                          settlement={settlements[co.id]}
                          canInvoice={pendingIds.includes(co.id)}
                          busy={busy}
                          payOpen={payingId === co.id}
                          onInvoice={() => onInvoice?.(co.id)}
                          onTogglePay={() => setPayingId(payingId === co.id ? null : co.id)}
                        />
                      )}
                      {onDelete && (
                        <DeleteButton
                          busy={busy}
                          onConfirm={() => {
                            setPayingId(null);
                            onDelete(co.id);
                          }}
                        />
                      )}
                    </div>
                  </Td>
                </tr>
                {payingId === co.id && settlements[co.id] && onRecordPayment && (
                  <tr>
                    <td colSpan={8} className="bg-neutral-50 px-4 py-4">
                      <RecordPaymentForm
                        settlement={settlements[co.id]!}
                        currency={currency}
                        noun="invoice"
                        dateLabel="Received on"
                        busy={busy}
                        onSubmit={(p) => {
                          onRecordPayment(settlements[co.id]!, p);
                          setPayingId(null);
                        }}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </Table>
        </>
      )}
    </Panel>
  );
}
